<script setup lang="ts">
  import { computed, nextTick, ref, watch, type Component } from 'vue'
  import { until, useMediaQuery } from '@vueuse/core'
  import { DialogDescription, DialogTitle } from 'reka-ui'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Button,
    Kbd,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
    useModalStack
  } from '@gat-ship/vue-components'
  import {
    IconChevronUp,
    IconClose,
    IconListTodo,
    IconMaximize,
    IconMinimize
  } from '@gat-ship/icons'
  import type { Stakeholder } from '@gat-ship/types'
  import { useCreateStakeholder } from '@gat-ship/stakeholder-create'
  import { useVisualViewportHeight } from '../../../../composables/useVisualViewportHeight'
  import { useCreatePortcallForm } from '../../../../composables/useCreatePortcallForm'
  import { usePortcallStepSources } from '../../../../composables/usePortcallStepSources'
  import { usePortcallPortReset } from '../../../../composables/usePortcallPortReset'
  import { usePortcallWizard } from '../../../../composables/usePortcallWizard'
  import { usePortcallStepFlowView } from '../../../../composables/usePortcallStepFlowView'
  import { useCreatePortcallCommands } from '../../../../composables/useCreatePortcallCommands'
  import { VESSEL_TBN } from '../../../../composables/usePortcallApiCalls'
  import type { EtaFieldValue } from '../../../../model/eta-bridge'
  import type {
    ArrivalStepKey,
    ListStepKey,
    PortcallStep,
    StepOption
  } from './create-portcall-steps'
  import PortcallStepSidebar from './PortcallStepSidebar.vue'
  import PortcallStepArrival from './PortcallStepArrival.vue'
  import PortcallStepAgency from './PortcallStepAgency.vue'
  import PortcallStepPrincipal from './PortcallStepPrincipal.vue'
  import PortcallStepVessel from './PortcallStepVessel.vue'
  import PortcallStepCallType from './PortcallStepCallType.vue'
  import PortcallStepPort from './PortcallStepPort.vue'
  import PortcallStepSummary from './PortcallStepSummary.vue'

  defineProps<{ fullScreen: boolean }>()
  const emit = defineEmits<{ close: []; toggleFullscreen: [] }>()

  const {
    form,
    formValues,
    useEtaAtPilotStation,
    loadingLocalPort,
    canSubmit,
    getValues,
    reset
  } = useCreatePortcallForm()

  const { sourceFor, addCreatedPrincipal, agencyPorts } =
    usePortcallStepSources(() => formValues.value.agencyId)
  const { createPortcall } = useCreatePortcallCommands()

  // A step's "Manage …" affordance opens the global Administration modal on the
  // matching panel. It stacks on top of this wizard via the central modal host,
  // so closing it returns the user to where they left off.
  const stack = useModalStack()
  function openAdminPanel(panel: string) {
    stack.open('admin', { panel })
  }

  // "Manage ports" opens the single-agency modal on its Ports tab, scoped to the
  // agency picked earlier in the wizard (the port step is gated behind the
  // agency step, so the id is set by the time this is reachable).
  function onManagePorts() {
    const id = formValues.value.agencyId
    if (!id) return
    stack.open('agency', { id, agencySection: 'ports' })
  }

  // The principal step's "Add client" affordance opens the create-stakeholder
  // modal (the wizard is wrapped in a `CreateStakeholderProvider` by its modal
  // host) pre-scoped to the client type.
  const { modals: stakeholderModals } = useCreateStakeholder()
  function onAddClient() {
    stakeholderModals.showModalStakeholderCreate({
      stakeholderType: 'client',
      onCreated: selectCreatedPrincipal
    })
  }

  const currentStepKey = ref<PortcallStep['key']>('agencyId')
  const done = ref(false)
  const creating = ref(false)
  /** The server-assigned call number, captured once the call is created. */
  const createdCallNumber = ref<string | null>(null)
  /** Display labels captured when a list option is chosen (for the sidebar). */
  const selectedLabels = ref<Record<string, string>>({})

  /** Which component renders each list step's body. */
  const LIST_STEP_COMPONENTS: Record<ListStepKey, Component> = {
    agencyId: PortcallStepAgency,
    appointingPrincipal: PortcallStepPrincipal,
    vesselImo: PortcallStepVessel,
    callType: PortcallStepCallType,
    portOfCallLocationCode: PortcallStepPort
  }

  // The wizard state machine (visible steps, gating, completeness, the arrival
  // requirement state) lives in the pure `model/portcall-wizard.ts`; this
  // composable is the MVVM seam.
  const wizard = usePortcallWizard({
    currentStepKey,
    formValues,
    pilotStationActive: useEtaAtPilotStation,
    canSubmit
  })
  const {
    visibleSteps,
    currentStep,
    currentIndex,
    isLastStep,
    nextVisible,
    prevVisible,
    canEnter,
    isStepComplete,
    findStep
  } = wizard

  // Sidebar + footer + arrival-banner view-model: the orchestrator binds it,
  // it doesn't compute it.
  const {
    sidebarSteps,
    createdSidebar,
    summaryRows,
    arrivalTitle,
    arrivalEyebrow,
    arrivalOptional,
    arrivalRequirement,
    showArrivalRequirement,
    isList,
    isArrival,
    isSummary,
    vesselUnset,
    progressText,
    enterHint,
    nextLabel,
    canNext
  } = usePortcallStepFlowView({
    wizard,
    formValues,
    selectedLabels,
    currentStepKey,
    done,
    createdCallNumber,
    pilotStationActive: useEtaAtPilotStation,
    loadingLocalPort
  })

  // Changing the agency invalidates the port-of-call: keep it only if the new
  // agency still serves it, otherwise take that agency's default port or clear.
  usePortcallPortReset({
    agencyPorts: () => agencyPorts.value,
    selectedPortCode: () => formValues.value.portOfCallLocationCode ?? '',
    setPort: (code, label) => {
      form.setFieldValue('portOfCallLocationCode', code)
      selectedLabels.value = {
        ...selectedLabels.value,
        portOfCallLocationCode: label
      }
    }
  })

  // Below the md breakpoint the step rail is hidden; the footer's progress
  // pill opens it as a bottom sheet instead. `isDesktop` matches Tailwind's
  // `md:` so markup and layout stay in lockstep.
  const isDesktop = useMediaQuery('(min-width: 768px)')
  // On mobile the shell tracks the visual viewport so the step (field + list +
  // footer) compresses into the space above the keyboard instead of hiding
  // behind it. Desktop keeps its CSS sizing (no keyboard, centred card).
  const viewportHeight = useVisualViewportHeight()
  // With the on-screen keyboard up on mobile, the visual viewport shrinks well
  // below the layout viewport, leaving room for only a couple of list rows. Detect
  // that and collapse the modal header + each step's question block so the list
  // gets the reclaimed space. Desktop has no soft keyboard, so it's never active.
  const keyboardOpen = computed(
    () => !isDesktop.value && viewportHeight.value < window.innerHeight - 120
  )
  const stepsOpen = ref(false)
  watch(isDesktop, (desktop) => {
    if (desktop) stepsOpen.value = false
  })

  /** Jump to a step from the mobile sheet, then dismiss it. */
  function onSheetSelect(key: string) {
    stepsOpen.value = false
    goStep(key)
  }

  function listValue(step: PortcallStep): string {
    return String(formValues.value[step.key as ListStepKey] ?? '')
  }
  function arrivalValue(step: PortcallStep): EtaFieldValue {
    return formValues.value[step.key as ArrivalStepKey]
  }
  // The orchestrator owns the sources, so it writes the search ref (the step
  // emits rather than mutating its `source` prop).
  function onListQuery(value: string) {
    sourceFor(currentStep.value.key as ListStepKey).query.value = value
  }

  // --- Navigation ---
  function goStep(key: string) {
    const step = findStep(key)
    if (!step || !canEnter(step)) return
    currentStepKey.value = step.key
    done.value = false
  }

  // Skipping the vessel step marks the vessel "to be named"; confirm first so it
  // isn't an accidental click (the sentinel can be cleared later via the step).
  const confirmSkipVesselOpen = ref(false)

  function confirmSkipVessel() {
    confirmSkipVesselOpen.value = false
    onListSelect({ value: VESSEL_TBN, label: 'TBN — To be named' }, true)
  }

  function goNext() {
    const step = currentStep.value
    if (vesselUnset.value) {
      confirmSkipVesselOpen.value = true
      return
    }
    if (!isStepComplete(step)) return
    if (isLastStep(step)) {
      void create()
      return
    }
    const next = nextVisible()
    if (next && canEnter(next)) currentStepKey.value = next.key
  }

  function goBack() {
    const prev = prevVisible()
    if (prev) currentStepKey.value = prev.key
  }

  // --- List-step selection ---
  // Selecting sets the value; the wizard only advances when the choice was
  // made with the keyboard (↵) or a double-click. A single mouse click selects
  // in place and leaves advancing to the Continue button. The vessel step's
  // "to be named" choice arrives here like any other option.
  function onListSelect(option: StepOption, advance: boolean) {
    const step = currentStep.value
    form.setFieldValue(step.key as ListStepKey, option.value)
    selectedLabels.value = {
      ...selectedLabels.value,
      [step.key]: option.label
    }
    if (advance) void advanceAfterListSelect(step)
  }

  // "Add client" creates a client in a stacked modal; on success the
  // create-stakeholder provider hands the new stakeholder back here via the
  // `onCreated` callback passed at open-time. Surface it in the principal list
  // (the server search won't return a just-created client) and select it in
  // place (no advance), matching a single-click selection.
  function selectCreatedPrincipal(stakeholder: Stakeholder) {
    const option: StepOption = {
      value: stakeholder.id,
      label: stakeholder.name,
      sub: stakeholder.shortName ?? undefined
    }
    addCreatedPrincipal(option)
    form.setFieldValue('appointingPrincipal', option.value)
    selectedLabels.value = {
      ...selectedLabels.value,
      appointingPrincipal: option.label
    }
  }

  // The port step's pilot-station visibility loads asynchronously; wait for it
  // to settle so keyboard-advancing from the port doesn't skip (or wrongly
  // show) the pilot-station arrival.
  async function advanceAfterListSelect(step: PortcallStep) {
    if (step.key === 'portOfCallLocationCode') {
      await nextTick()
      await until(loadingLocalPort).toBe(false)
    }
    goNext()
  }

  function onArrivalUpdate(value: EtaFieldValue) {
    form.setFieldValue(currentStep.value.key as ArrivalStepKey, value)
  }

  // --- Submit ---
  async function create() {
    if (creating.value) return
    const payload = getValues()
    if (!payload) return
    creating.value = true
    try {
      const created = await createPortcall(payload)
      createdCallNumber.value = created.callNumber
      done.value = true
    } finally {
      creating.value = false
    }
  }

  function createAnother() {
    reset()
    selectedLabels.value = {}
    currentStepKey.value = 'agencyId'
    createdCallNumber.value = null
    done.value = false
  }

  // Keyboard: list steps are driven by the Command listbox (↑↓ + ↵); arrival
  // steps advance on Enter unless focus is inside a combobox/listbox popup.
  function onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || done.value) return
    if (!isArrival.value && !isSummary.value) return
    const target = event.target as HTMLElement | null
    if (
      target?.getAttribute('role') === 'combobox' ||
      target?.closest('[role="listbox"]')
    ) {
      return
    }
    if (canNext.value) {
      event.preventDefault()
      goNext()
    }
  }

  // Safety: if the active step becomes hidden (port changed to one without a
  // pilot station while sitting on it), fall back to the last visible step.
  watch(visibleSteps, (steps) => {
    if (done.value) return
    if (!steps.some((s) => s.key === currentStepKey.value)) {
      currentStepKey.value = steps[steps.length - 1]?.key ?? 'agencyId'
    }
  })
</script>

<template>
  <div
    :class="[
      'relative flex flex-col overflow-hidden',
      fullScreen
        ? 'h-dvh w-screen'
        : 'h-dvh w-full md:h-[720px] md:max-h-[88vh] md:w-[880px] md:max-w-[94vw]'
    ]"
    :style="isDesktop ? undefined : { height: `${viewportHeight}px` }"
    @keydown="onKeydown"
  >
    <!-- header: hidden (via v-show, keeping the DialogTitle mounted for a11y)
         while the mobile keyboard is up so the list gets the space -->
    <div
      v-show="!keyboardOpen"
      class="flex flex-none items-center justify-between border-b px-5 py-4"
    >
      <div>
        <DialogTitle class="text-[15px] font-semibold leading-none">
          New Port Call
        </DialogTitle>
        <DialogDescription class="text-muted-foreground mt-1.5 text-xs">
          <template v-if="done">Port call created</template>
          <template v-else>
            {{ visibleSteps.length }} quick steps · ~2 minutes to complete
          </template>
        </DialogDescription>
      </div>
      <div class="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          class="hidden md:inline-flex"
          :aria-label="fullScreen ? 'Exit full screen' : 'Full screen'"
          @click="emit('toggleFullscreen')"
        >
          <IconMinimize v-if="fullScreen" class="size-4" />
          <IconMaximize v-else class="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Close"
          @click="emit('close')"
        >
          <IconClose class="size-4" />
        </Button>
      </div>
    </div>

    <!-- body: resizable step rail + content. The handle is drag-only on
         desktop; on touch it's hidden so the rail stays a fixed proportion.
         Widths persist to localStorage via the auto-save id. -->
    <ResizablePanelGroup
      direction="horizontal"
      auto-save-id="create-portcall-modal-sidebar-v2"
      class="min-h-0 flex-1"
    >
      <!-- Sidebar is sized in real pixels (not a percent of the modal) so its
           min stays constant regardless of full-screen width. Hidden below md:
           on mobile it moves into the footer-triggered bottom sheet. -->
      <ResizablePanel
        v-if="isDesktop"
        size-unit="px"
        :default-size="256"
        :min-size="176"
        :max-size="384"
      >
        <PortcallStepSidebar
          :steps="sidebarSteps"
          :created="createdSidebar"
          @select="goStep"
        />
      </ResizablePanel>

      <ResizableHandle v-if="isDesktop" />

      <ResizablePanel
        :default-size="73"
        class="@container flex min-w-0 flex-col"
      >
        <!-- each step body owns its fixed header; only its content region
             scrolls. The summary step renders in both the pre- and
             post-create states (it shows the success banner once created).
             Once the panel is wide enough, the body caps at 500px and centres
             so lines stay readable in full-screen (container query on the
             panel above). -->
        <div
          class="@[640px]:max-w-[600px] mx-auto flex min-h-0 w-full flex-1 flex-col"
        >
          <component
            :is="LIST_STEP_COMPONENTS[currentStep.key as ListStepKey]"
            v-if="isList"
            :source="sourceFor(currentStep.key as ListStepKey)"
            :selected-value="listValue(currentStep)"
            :keyboard-open="keyboardOpen"
            @select="onListSelect"
            @update:query="onListQuery"
            @manage-agencies="openAdminPanel('agency-structure')"
            @manage-ports="onManagePorts"
            @add-client="onAddClient"
          />

          <PortcallStepSummary
            v-else-if="isSummary"
            :rows="summaryRows"
            :done="done"
          />

          <template v-else>
            <div class="flex-none px-6 pb-4 pt-6">
              <ModalSectionHeader class="gap-0">
                <div
                  class="text-muted-foreground mb-2 text-[11px] font-semibold uppercase tracking-wider"
                >
                  {{ arrivalEyebrow }}
                  <span
                    v-if="arrivalOptional"
                    class="text-muted-foreground ml-0.5 font-normal normal-case tracking-normal"
                  >
                    (optional)
                  </span>
                </div>
                <ModalSectionHeaderTitle
                  class="text-lg font-semibold leading-tight"
                >
                  {{ arrivalTitle }}
                </ModalSectionHeaderTitle>
              </ModalSectionHeader>
            </div>
            <div class="min-h-0 flex-1 overflow-auto px-6 pb-6">
              <PortcallStepArrival
                :key="currentStep.key"
                :model-value="arrivalValue(currentStep)"
                :disabled="loadingLocalPort"
                :show-requirement="showArrivalRequirement"
                :req-state="arrivalRequirement.state"
                :req-title="arrivalRequirement.title"
                :req-sub="arrivalRequirement.sub"
                @update:model-value="onArrivalUpdate"
              />
            </div>
          </template>
        </div>

        <!-- footer: full-width divider, but its inner row tracks the same
             centred content column so the actions line up with the section
             above; it stays pinned while the body scrolls. Hidden with the
             keyboard up on mobile (like the header) to give the list the room;
             dismissing the keyboard brings it back to reach Continue. -->
        <div v-show="!keyboardOpen" class="flex-none border-t">
          <div
            class="mx-auto flex w-full items-center justify-between px-6 py-3.5"
            :class="fullScreen && 'max-w-[600px]'"
          >
            <!-- mobile: tap the progress to open the step list; desktop keeps
                 the plain text since the rail is always visible there -->
            <button
              v-if="!isDesktop && !done"
              type="button"
              class="text-muted-foreground hover:bg-muted -mx-2 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs"
              @click="stepsOpen = true"
            >
              <IconListTodo class="size-3.5" />
              {{ progressText }}
              <IconChevronUp class="size-3.5" />
            </button>
            <span v-else class="text-muted-foreground text-xs">
              <template v-if="done">Port call created</template>
              <template v-else>{{ progressText }}</template>
            </span>
            <div class="flex items-center gap-2">
              <template v-if="done">
                <Button
                  variant="outline"
                  :disabled="creating"
                  @click="createAnother"
                >
                  Create another
                </Button>
                <!-- TODO: wire navigation to the created port call (needs the
                   whiteboard/port-call route + created call number). -->
                <Button disabled>Go to Port Call</Button>
              </template>
              <template v-else>
                <span
                  class="text-muted-foreground mr-1 hidden items-center gap-1.5 text-[11px] md:flex"
                >
                  <template v-if="isList">
                    Type to filter ·
                    <Kbd>↑↓</Kbd>
                    move ·
                    <Kbd>↵</Kbd>
                    select
                  </template>
                  <template v-else>
                    <Kbd>↵</Kbd>
                    {{ enterHint }}
                  </template>
                </span>
                <Button
                  v-if="currentIndex > 0"
                  variant="outline"
                  @click="goBack"
                >
                  Back
                </Button>
                <Button :disabled="!canNext || creating" @click="goNext">
                  {{ nextLabel }}
                </Button>
              </template>
            </div>
          </div>
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>

    <!-- Mobile step menu: an in-modal bottom sheet. Kept inside the dialog's
         own stacking context (not a portaled Drawer) so it can never fall
         behind this host-stacked, non-modal dialog. Opened from the footer
         progress pill; selecting a step jumps and dismisses. -->
    <Transition name="steps-sheet">
      <div v-if="stepsOpen && !isDesktop" class="absolute inset-0 z-20">
        <div class="absolute inset-0 bg-black/40" @click="stepsOpen = false" />
        <div
          class="steps-sheet-panel bg-background absolute inset-x-0 bottom-0 flex max-h-[80%] flex-col rounded-t-2xl shadow-xl"
        >
          <div class="flex-none px-4 pb-1 pt-3">
            <div class="bg-muted mx-auto h-1.5 w-10 rounded-full" />
            <div
              class="text-muted-foreground mt-3 text-[11px] font-semibold uppercase tracking-wider"
            >
              Steps
            </div>
          </div>
          <div class="min-h-0 flex-1 overflow-hidden">
            <PortcallStepSidebar
              :steps="sidebarSteps"
              :created="createdSidebar"
              @select="onSheetSelect"
            />
          </div>
        </div>
      </div>
    </Transition>
  </div>

  <AlertDialog v-model:open="confirmSkipVesselOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Set the vessel to "To be named"?</AlertDialogTitle>
        <AlertDialogDescription>
          No vessel will be assigned to this port call yet — it'll be marked as
          TBN (to be named). You can set the actual vessel later from the port
          call.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <Button variant="outline" @click="confirmSkipVesselOpen = false">
          Cancel
        </Button>
        <Button @click="confirmSkipVessel">Set to TBN</Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped>
  /* Mobile step sheet: the backdrop fades while the panel slides up from the
     bottom. Honour reduced-motion by skipping the transform. */
  .steps-sheet-enter-active,
  .steps-sheet-leave-active {
    transition: opacity 200ms ease;
  }
  .steps-sheet-enter-active .steps-sheet-panel,
  .steps-sheet-leave-active .steps-sheet-panel {
    transition: transform 260ms cubic-bezier(0.32, 0.72, 0, 1);
  }
  .steps-sheet-enter-from,
  .steps-sheet-leave-to {
    opacity: 0;
  }
  .steps-sheet-enter-from .steps-sheet-panel,
  .steps-sheet-leave-to .steps-sheet-panel {
    transform: translateY(100%);
  }

  @media (prefers-reduced-motion: reduce) {
    .steps-sheet-enter-active .steps-sheet-panel,
    .steps-sheet-leave-active .steps-sheet-panel {
      transition: none;
    }
    .steps-sheet-enter-from .steps-sheet-panel,
    .steps-sheet-leave-to .steps-sheet-panel {
      transform: none;
    }
  }
</style>
